import { useEffect, useRef } from 'react';
import { useStore } from '@/store';
import { api } from '@/lib/api';

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { isAuthenticated, accessToken, tokenExpiry, setLoading } = useStore();
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasInitialized = useRef(false);

  // Silent refresh on app startup
  useEffect(() => {
    if (hasInitialized.current) return;
    hasInitialized.current = true;

    const initializeAuth = async () => {
      // Skip silent refresh on logout-related paths to prevent re-authentication
      const currentPath = window.location.pathname;
      const isLogoutPath = currentPath === '/logout-success' || currentPath === '/logout';

      if (isLogoutPath) {
        console.debug('Skipping silent refresh on logout path:', currentPath);
        setLoading(false);
        return;
      }

      // If no access token is present, attempt a silent refresh
      // This will work if the user has a valid refresh token cookie
      if (!accessToken && !isAuthenticated) {
        try {
          const response = await api.post<{ accessToken: string; expiresIn?: number; user?: any }>(
            'auth/refresh'
          );

          if (response.accessToken && response.user) {
            const store = useStore.getState();
            store.login(response.user, response.accessToken, response.expiresIn);
          } else {
            // No valid session - mark as not loading
            setLoading(false);
          }
        } catch (error) {
          // Silent fail - user is not authenticated
          console.debug('Silent refresh failed - user not authenticated');
          setLoading(false);
        }
      } else {
        // Already has token or is authenticated - mark as not loading
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Once authenticated, load organization/workspaces so UI can render tracking email and data
  useEffect(() => {
    const loadWorkspaces = async () => {
      const state = useStore.getState();
      if (isAuthenticated && state.workspaces.length === 0 && !state.isLoadingWorkspaces) {
        try {
          await state.fetchWorkspaces();
        } catch (e) {
          console.error('Failed to fetch workspaces after auth:', e);
        }
      }
    };
    loadWorkspaces();
  }, [isAuthenticated]);

  // Sliding session refresh mechanism
  useEffect(() => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // If not authenticated or no token expiry, don't schedule refresh
    if (!isAuthenticated || !tokenExpiry) {
      return;
    }

    // Calculate when to refresh (5 minutes before expiry)
    const now = Date.now();
    const refreshTime = tokenExpiry - 5 * 60 * 1000; // 5 minutes before expiry
    const delay = Math.max(0, refreshTime - now);

    // If token is already close to expiry or expired, refresh immediately
    if (delay === 0) {
      performTokenRefresh();
    } else {
      // Schedule refresh
      refreshTimerRef.current = setTimeout(() => {
        performTokenRefresh();
      }, delay);
    }

    // Cleanup function
    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [isAuthenticated, tokenExpiry]);

  const performTokenRefresh = async () => {
    try {
      const response = await api.post<{ accessToken: string; expiresIn?: number; user?: any }>(
        'auth/refresh'
      );

      if (response.accessToken) {
        const store = useStore.getState();
        store.updateTokens(response.accessToken, response.expiresIn);

        // Update user if returned
        if (response.user) {
          store.setUser(response.user);
        }
      }
    } catch (error) {
      console.error('Token refresh failed:', error);
      // The API client will handle logout on 401
    }
  };

  // Also refresh on window focus for better UX
  useEffect(() => {
    const handleFocus = () => {
      const store = useStore.getState();
      // Only refresh if token is expired or about to expire (within 1 minute)
      if (store.isAuthenticated && store.tokenExpiry) {
        const timeUntilExpiry = store.tokenExpiry - Date.now();
        if (timeUntilExpiry < 60 * 1000) {
          // Less than 1 minute
          performTokenRefresh();
        }
      }
    };

    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  return <>{children}</>;
}
